import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  computeOverdueInvoices,
  markInvoicePaid,
  watchCustomers,
  watchInvoices,
} from '../lib/data.js'
import { fmtDateTime, fmtMoney } from '../lib/format.js'
import { sendInvoiceReminder } from '../lib/stripe.js'

export default function Overdue() {
  const [customers, setCustomers] = useState({})
  const [invoices, setInvoices] = useState({})
  const [busyId, setBusyId] = useState(null)
  const [flash, setFlash] = useState(null)
  useEffect(() => watchCustomers(setCustomers), [])
  useEffect(() => watchInvoices(setInvoices), [])

  const list = computeOverdueInvoices(invoices, customers)

  const onSendReminder = async (row) => {
    if (busyId) return
    setBusyId(row.id)
    setFlash(null)
    try {
      // First reminder unless final notice already went out; admin click
      // always escalates if a final hasn't been sent yet.
      const stage = row.reminderStage === 'final' ? 'final' : (row.reminderStage === 'first' ? 'final' : 'first')
      await sendInvoiceReminder(row.id, stage)
      setFlash({ kind: 'ok', msg: `${stage === 'final' ? 'Final notice' : 'Reminder'} sent to ${row.customer?.email || 'customer'}.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message || 'Could not send reminder.' })
    } finally {
      setBusyId(null)
    }
  }

  const onMarkPaid = async (invoiceId) => {
    if (!confirm('Mark this invoice as paid? It will be removed from the overdue list.')) return
    setBusyId(invoiceId)
    try {
      await markInvoicePaid(invoiceId, { manualPayment: true })
      setFlash({ kind: 'ok', msg: 'Marked as paid.' })
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message || 'Could not mark paid.' })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-extrabold">Overdue</h1>
        {list.length > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-extrabold">
            {list.length}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-gray-600">
        Manual/invoice customers (no auto-draft) with an unpaid invoice past its due date. The system automatically sends a reminder on day 2 and a final shutoff-warning notice on day 4 from <b>yesnet19@gmail.com</b>.
      </p>

      {flash ? (
        <div className={`rounded-lg border px-3 py-2 text-sm ${flash.kind === 'ok' ? 'bg-brand-mint border-brand-green text-brand-green-deep' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {flash.msg}
        </div>
      ) : null}

      <div className="card p-0 overflow-hidden">
        {list.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            🎉 No overdue invoices.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-center px-4 py-2">Days late</th>
                  <th className="text-left px-4 py-2">Last reminder</th>
                  <th className="text-center px-4 py-2">Final notice</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => {
                  const days = daysOverdue(row.dueDate)
                  return (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <Link to={`/customers/${row.customerId}`} className="font-bold hover:underline">
                          {row.customer?.name || row.customerName || '—'}
                        </Link>
                        <div className="text-xs text-gray-500">#{row.invoiceNumber} · due {row.dueDate || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold">{fmtMoney(row.amount)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-extrabold ${
                          days >= 4 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {days}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {row.lastReminderAt ? fmtDateTime(row.lastReminderAt) : '—'}
                        {row.reminderStage && row.reminderStage !== 'none' ? (
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{row.reminderStage}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.shutoffWarningAt ? (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-widest">⚠ Sent</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => onSendReminder(row)}
                          disabled={busyId === row.id}
                          className="btn-secondary text-xs px-3 py-1.5 mr-2">
                          {busyId === row.id ? '…' : 'Send Reminder'}
                        </button>
                        <button
                          onClick={() => onMarkPaid(row.id)}
                          disabled={busyId === row.id}
                          className="btn-gold text-xs px-3 py-1.5">
                          Mark Paid
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function daysOverdue(dueDate) {
  if (!dueDate) return 0
  const due = new Date(dueDate + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - due.getTime()) / 86_400_000)
}
